import { AfterViewInit, Component, ViewChild, Input } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import {
    ValueMetricsOperationType,
    ValueMetricsProductType
} from '../../../../constants-types';
import {
    ModuleName,
    TransactionTableEntry,
    ValueMetricLimit,
    ValueMetricTransactionQuery,
    ValueMetricTransactionUser
} from '../../../../core-data-model';
import { ValueMetricsService } from '../../../../core-services';
import { GuidUtilities } from '../../../../core-utilities';
import {
    ModuleUserAccessService,
    UserProfileSettingsService,
    ModuleAccessService
} from '../../../../system-services';
import { TranslationService } from '../../../../translations';

@Component({
    selector: 'transaction-table-component',
    template: `
        <div>
            <div>
                <span class="subscription-plan-overview-title-text">
                    {{
                        TR.key(
                            'subscription-manager.overview.user-activity-title'
                        )
                    }}
                </span>
                <span class="product-radio layoutHorizontal">
                    <span class="subscription-plan-overview-title-text">
                        {{
                            TR.key(
                                'subscription-manager.overview.user-activity-radio-title'
                            )
                        }}
                    </span>
                    <span>
                        <form>
                            @for (filter of filters; track filter) {
                                <div class="inline-radio">
                                    <input
                                        type="radio"
                                        name="selectedProduct"
                                        (click)="radioChange(filter.value)"
                                        [checked]="
                                            filter.checked === productDisplayed
                                        "
                                    />
                                    <span class="radio-choice">
                                        {{ filter.text }}
                                    </span>
                                </div>
                            }
                        </form>
                    </span>
                </span>
            </div>
            <div class="transaction-table">
                <table
                    class="table"
                    mat-table
                    [dataSource]="matTableData"
                    matSort
                    #transTableSort="matSort"
                    matSortActive="date"
                    matSortDirection="desc"
                >
                    <ng-container matColumnDef="date">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header>
                            {{
                                TR.key(
                                    'subscription-manager.transaction-table-header-date'
                                )
                            }}
                        </th>
                        <td mat-cell *matCellDef="let item">
                            {{ item.date }}
                        </td>
                    </ng-container>
                    <ng-container matColumnDef="name">
                        <th mat-header-cell *matHeaderCellDef>
                            {{
                                TR.key(
                                    'subscription-manager.transaction-table-header-user'
                                )
                            }}
                        </th>
                        <td mat-cell *matCellDef="let item">{{ item.name }}</td>
                    </ng-container>
                    <ng-container matColumnDef="action">
                        <th mat-header-cell *matHeaderCellDef>
                            {{
                                TR.key(
                                    'subscription-manager.transaction-table-header-action'
                                )
                            }}
                        </th>
                        <td mat-cell *matCellDef="let item">
                            {{ item.action }}
                        </td>
                    </ng-container>
                    <ng-container matColumnDef="module">
                        <th mat-header-cell *matHeaderCellDef>
                            {{
                                TR.key(
                                    'subscription-manager.transaction-table-header-module'
                                )
                            }}
                        </th>
                        <td mat-cell *matCellDef="let item">
                            {{ item.module }}
                        </td>
                    </ng-container>
                    <tr
                        class="transaction-table-mat-header-row"
                        mat-header-row
                        *matHeaderRowDef="displayedColumns; sticky: true"
                    ></tr>
                    <tr
                        class="transaction-table-mat-cell-row"
                        mat-row
                        *matRowDef="let row; columns: displayedColumns"
                    ></tr>
                    <!--Empty table row-->
                    <tr *matNoDataRow>
                        <td colspan="4">
                            <transaction-table-empty
                                [product]="productDisplayed"
                                [loading]="loading"
                            ></transaction-table-empty>
                        </td>
                    </tr>
                </table>
            </div>
        </div>
    `,
    styleUrls: [
        '../overview.component.less',
        './transaction-table.component.less'
    ],
    standalone: false
})
export class TransactionTableComponent implements AfterViewInit {
    @ViewChild('transTableSort', { static: false }) tableSort = new MatSort();
    @Input()
    vmConfigs: ValueMetricLimit[] = [];
    ModuleName = ModuleName;
    allTransactions: TransactionTableEntry[] = [];
    displayedColumns = ['date', 'name', 'action', 'module'];
    matTableData: MatTableDataSource<TransactionTableEntry> =
        new MatTableDataSource<TransactionTableEntry>(this.allTransactions);
    productDisplayed: ModuleName = ModuleName.Unknown;
    loading: boolean;
    filters = [];
    guid: GuidUtilities = new GuidUtilities();

    constructor(
        public TR: TranslationService,
        public moduleAccess: ModuleAccessService,
        private userProfile: UserProfileSettingsService,
        private VM: ValueMetricsService,
        private moduleUserAccess: ModuleUserAccessService
    ) {}

    getFilters() {
        this.filters = [
            {
                value: 'All',
                text: this.TR.key(
                    'subscription-manager.overview.user-activity-radio-option-all'
                ),
                checked: ModuleName.Unknown
            }
        ];
        const products = Array.from(
            new Set(this.matTableData.data.map((item) => item.module)).values()
        );
        products.forEach((product) => {
            const productType = this.getProductType(product);
            if (this.isAdminForModule(this.ProductModuleMap[productType])) {
                this.filters.push({
                    value: this.ProductNameMap[productType],
                    text: this.FilterTextMap[productType],
                    checked: this.CheckedMap[productType]
                });
            }
        });
    }

    isAdminForModule(module: ModuleName): boolean {
        return this.moduleUserAccess.hasModuleAccessLevelOrAbove(
            module,
            PermissionsModuleAccessLevel.AllCompanyAdmin
        );
    }

    async ngAfterViewInit() {
        this.loading = true;

        const query: ValueMetricTransactionQuery = {} as any;
        query.companyGuid = this.userProfile.currentCompany;
        const startDate = new Date();
        startDate.setFullYear(startDate.getFullYear() - 1);
        query.startDate = startDate.toISOString();
        query.operationType = ValueMetricsOperationType.Consume;
        const transactions = await this.VM.getValueMetricTransactions(query);

        transactions.forEach((transaction) => {
            const entry: TransactionTableEntry = {
                date: this.getDateString(transaction.eventTime),
                name: this.getName(transaction.createdByUser),
                action: this.getAction(transaction.product),
                module: this.ProductNameMap[transaction.product]
            };
            this.allTransactions.push(entry);
        });
        this.radioChange('All');
        this.matTableData = new MatTableDataSource(this.allTransactions);
        this.matTableData.sort = this.tableSort;
        this.matTableData.sortingDataAccessor = (item, property) =>
            property === 'date' ? new Date(item.date) : item[property];
        this.loading = false;

        this.getFilters();
    }

    getAction(product: ValueMetricsProductType): string {
        switch (product) {
            case ValueMetricsProductType.HireHero: {
                return 'Candidate Activation';
            }
            case ValueMetricsProductType.Design2: {
                return 'Team Activation';
            }
        }
    }

    getProductType(product: string): ValueMetricsProductType {
        switch (product) {
            case 'Hire': {
                return ValueMetricsProductType.HireHero;
            }
            case 'Design': {
                return ValueMetricsProductType.Design2;
            }
            default: {
                return ValueMetricsProductType.Unknown;
            }
        }
    }

    getDateString(date: string): string {
        return new Date(date).toLocaleDateString();
    }

    getName(user: ValueMetricTransactionUser): string {
        if (user && user.userGuid != this.guid.getEmpty()) {
            return user.firstName + ' ' + user.lastName;
        } else {
            return this.TR.key(
                'subscription-manager.overview.user-activity-table.user-not-found'
            );
        }
    }

    radioChange(product: string) {
        switch (product) {
            case 'Hire': {
                this.productDisplayed = ModuleName.HireHero;
                this.matTableData.filter = 'Hire';
                break;
            }
            case 'Design': {
                this.productDisplayed = ModuleName.Design2;
                this.matTableData.filter = 'Design';
                break;
            }
            default: {
                this.productDisplayed = ModuleName.Unknown;
                this.matTableData.filter = '';
                break;
            }
        }
    }

    private readonly FilterTextMap: { [key: string]: string } = {
        [ValueMetricsProductType.HireHero]: this.TR.key(
            'subscription-manager.overview.user-activity-radio-option-hire'
        ),
        [ValueMetricsProductType.Design2]: this.TR.key(
            'subscription-manager.overview.user-activity-radio-option-design'
        )
    };

    private readonly ProductNameMap: {
        [key: string]: string;
    } = {
        [ValueMetricsProductType.HireHero]: 'Hire',
        [ValueMetricsProductType.Design2]: 'Design'
    };

    private readonly ProductModuleMap: { [key: string]: ModuleName } = {
        [ValueMetricsProductType.HireHero]: ModuleName.Hire,
        [ValueMetricsProductType.Design2]: ModuleName.Design
    };

    private readonly CheckedMap: { [key: string]: ModuleName } = {
        [ValueMetricsProductType.HireHero]: ModuleName.HireHero,
        [ValueMetricsProductType.Design2]: ModuleName.Design2
    };
}
